import {
  AdminSwitch,
  Box,
  Button,
  ChevronSmall,
  CollapsibleComponent,
  Disclaimer,
  Div,
  H3,
  H5,
  Icon,
  Modal,
  P,
  SimpleBrandEntity,
  SimpleBrandWithImageEntity,
  SimpleLocationEntity,
  lightTheme,
} from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';

const ExportLocationItem = ({
  location,
  brands,
  showBrands,
}: {
  location: SimpleLocationEntity;
  brands: SimpleBrandWithImageEntity[];
  showBrands: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const canExpand = showBrands && brands.length > 0;

  useEffect(() => {
    if (!canExpand) setExpanded(false);
  }, [canExpand]);

  const handleToggle = () => {
    if (!canExpand) return;
    setHeight(ref.current?.clientHeight ?? 0);
    setExpanded((prev) => !prev);
  };

  return (
    <Box $mb="8" $minHeight="48px">
      <Div
        $flex
        $alignCenter
        $between
        $pointer={canExpand}
        onClick={handleToggle}
      >
        <Div>
          <P $m="0" $bold>
            {location.name}
          </P>
          {location.address && (
            <Disclaimer $color={lightTheme.lightGray} $mb="0">
              {location.address}
            </Disclaimer>
          )}
        </Div>
        {canExpand && (
          <Icon $rotate={expanded ? '180deg' : '0'}>
            <ChevronSmall />
          </Icon>
        )}
      </Div>

      {canExpand && (
        <CollapsibleComponent
          expanded={expanded}
          elementHeight={height}
          marginTop="8px"
        >
          <div ref={ref}>
            <Disclaimer $m="0">Brands:</Disclaimer>
            {brands.map((brand) => (
              <Disclaimer key={brand.id} $m="0" $color={lightTheme.lightGray}>
                • {brand.name}
              </Disclaimer>
            ))}
          </div>
        </CollapsibleComponent>
      )}
    </Box>
  );
};

export const ExportReportModal = ({
  isOpen,
  closeModal,
  onConfirm,
  dateLabel,
  selectedLocations,
  selectedBrands = [],
  statusLabel,
  query,
  includePhysicalLocationsOnly = false,
}: {
  isOpen: boolean;
  closeModal: () => void;
  onConfirm: (includePhysicalLocationsOnly: boolean) => void;
  dateLabel: string;
  selectedLocations: SimpleLocationEntity[];
  selectedBrands?: SimpleBrandEntity[];
  statusLabel?: string | null;
  query?: string | null;
  includePhysicalLocationsOnly?: boolean;
}) => {
  const [includeBrands, setIncludeBrands] = useState(
    !includePhysicalLocationsOnly
  );

  useEffect(() => {
    if (isOpen) setIncludeBrands(!includePhysicalLocationsOnly);
  }, [isOpen, includePhysicalLocationsOnly]);

  // When brands are selected in the filter, only show those (per location);
  // otherwise show every brand the location has.
  const getLocationBrands = (location: SimpleLocationEntity) =>
    selectedBrands.length > 0
      ? location.brands.filter((b) =>
          selectedBrands.some((sb) => sb.id === b.id)
        )
      : location.brands;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      exitButton={false}
      textAlign="left"
    >
      <H3 $m="10px" $center>
        Export Report
      </H3>
      <P $mb="24" $center>
        Generate a CSV report with the following filters?
      </P>

      <Div $mb="24">
        <Box $flex $alignCenter $between $mb="8" $minHeight="48px">
          <P $m="0">Date</P>
          <P $m="0" $bold>
            {dateLabel}
          </P>
        </Box>
        {statusLabel && (
          <Box $flex $alignCenter $between $mb="8" $minHeight="48px">
            <P $m="0">Status</P>
            <P $m="0" $bold>
              {statusLabel}
            </P>
          </Box>
        )}
        {query && (
          <Box $flex $alignCenter $between $mb="8" $minHeight="48px">
            <P $m="0">Search</P>
            <P $m="0" $bold>
              {query}
            </P>
          </Box>
        )}
      </Div>

      <Div $flex $alignCenter $between $m="32px 0">
        <H5 $m="0">Include Brands?</H5>

        <AdminSwitch
          checked={includeBrands}
          onChange={() => setIncludeBrands((prev) => !prev)}
        />
      </Div>

      {selectedLocations.length > 0 && (
        <Div $mb="24">
          <H5 $mb="8" $center>
            {selectedLocations.length > 1 ? 'Locations' : 'Location'}
          </H5>
          {selectedLocations.map((location) => (
            <ExportLocationItem
              key={location.id}
              location={location}
              brands={getLocationBrands(location)}
              showBrands={includeBrands}
            />
          ))}
        </Div>
      )}

      <Div $flex $gap="16px">
        <Button
          onClick={() => {
            onConfirm(!includeBrands);
            closeModal();
          }}
          $w="100%"
          $selected
        >
          Yes
        </Button>
        <Button $w="100%" onClick={closeModal} $gray $selected>
          No
        </Button>
      </Div>
    </Modal>
  );
};
